import { useMemo } from "react";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import { WAFFLE_FLAG_RATING_DISPLAY } from "@shared/constants/waffle-flags";
import {
  shouldDisplayRating,
  getDisplayableRating,
} from "@shared/modules/ratings/utils/ratings";
import type { PublicRatingSerializer } from "@shared/modules/ratings/types/ratings";
import type { FormattedRating } from "@shared/modules/ratings/types/ratings";

interface UseEventRatingsOptions {
  eventRating?: PublicRatingSerializer;
  venueRating?: PublicRatingSerializer;
  venueId?: number;
  /** When provided from SSR, used immediately to avoid layout shift while window.waffle loads */
  isRatingDisplayEnabled?: boolean;
}

interface UseEventRatingsResult {
  isCustomerReviewsEnabled: boolean;
  rating: FormattedRating | null;
  hasDisplayableRating: boolean;
}

/**
 * Feature-flag gating and display formatting for star ratings.
 *
 * Determines whether ratings UI should be visible based on waffle flags
 * (`enable_customer_reviews`, `enable_rating_display`) and computes a
 * display-ready formatted rating (event → venue fallback).
 *
 * When `isRatingDisplayEnabled` is provided (resolved via RedisWaffle in
 * getServerSideProps), it is used immediately so the rating renders on first
 * paint without waiting for window.waffle to hydrate.
 *
 * Does NOT fetch review data. For the paginated list of individual review
 * comments, use `useCustomerReviewsData` instead.
 */
export function useEventRatings({
  eventRating,
  venueRating,
  venueId,
  isRatingDisplayEnabled,
}: UseEventRatingsOptions): UseEventRatingsResult {
  const { isFlagActive } = useDjangoWaffle();

  const isCustomerReviewsEnabled = isFlagActive(
    "enable_customer_reviews",
    venueId
  );

  const canDisplayRating =
    isRatingDisplayEnabled ?? isFlagActive(WAFFLE_FLAG_RATING_DISPLAY, venueId);

  const rating = useMemo(
    () =>
      canDisplayRating ? getDisplayableRating(eventRating, venueRating) : null,
    [canDisplayRating, eventRating, venueRating]
  );

  const hasDisplayableRating = useMemo(
    () => shouldDisplayRating(venueRating) || shouldDisplayRating(eventRating),
    [venueRating, eventRating]
  );

  return {
    isCustomerReviewsEnabled,
    rating,
    hasDisplayableRating,
  };
}
